import React from 'react';
import PropTypes from 'prop-types';
import { Box, InputBase, Typography } from '@mui/material';
import config from '../config';
import { ALERT_APPEARANCES, ALERT_ICONS } from './Alert.model';

const { DEFAULT_LANGUAGE } = config;

// Until an urgency is picked, the preview uses the Normal look.
const DEFAULT_APPEARANCE = ALERT_APPEARANCES[1];

const styles = {
  banner: (appearance) => ({
    alignItems: 'center',
    backgroundColor: appearance.background,
    borderRadius: '12px',
    display: 'flex',
    gap: '20px',
    maxWidth: 500,
    px: 2,
    py: 1.5,
    width: '100%',
  }),
  icon: (appearance) => ({
    color: appearance.accent,
    flexShrink: 0,
    fontSize: 24,
  }),
  text: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  headline: (appearance) => ({
    color: appearance.accent,
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '24px',
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    '& textarea::placeholder': { color: appearance.accent, opacity: 1 },
  }),
  details: (appearance) => ({
    color: appearance.description,
    fontSize: '14px',
    lineHeight: '20px',
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    '& textarea::placeholder': { color: appearance.description, opacity: 1 },
  }),
  cta: (appearance) => ({
    alignItems: 'center',
    border: `1px solid ${appearance.accent}`,
    borderRadius: '48px',
    color: appearance.accent,
    display: 'flex',
    flexShrink: 0,
    fontSize: '14px',
    fontWeight: 700,
    height: 36,
    justifyContent: 'center',
    px: 2,
  }),
};

// The headline and details are typed straight into the banner when `onChange`
// is given; without it the banner is a read-only summary.
export default function AlertPreview({ alert, language, onChange }) {
  const appearance =
    ALERT_APPEARANCES.find(({ value }) => value === alert.appearance) ||
    DEFAULT_APPEARANCE;
  const { Icon } = ALERT_ICONS.find(({ name }) => name === alert.icon);
  const copy = alert.languages?.[language] || {};

  function renderText(field, placeholder, sx) {
    if (!onChange) {
      return <Typography sx={sx(appearance)}>{copy[field]}</Typography>;
    }

    return (
      <InputBase
        fullWidth
        multiline
        id={`alert-${field}`}
        inputProps={{ 'aria-label': placeholder }}
        onChange={(event) => onChange(field, event.target.value)}
        placeholder={placeholder}
        sx={sx(appearance)}
        value={copy[field] || ''}
      />
    );
  }

  return (
    <Box sx={styles.banner(appearance)}>
      <Icon sx={styles.icon(appearance)} />
      <Box sx={styles.text}>
        {renderText(
          'headline',
          'Click to enter a headline...',
          styles.headline
        )}
        {renderText(
          'details',
          'Click to enter a description...',
          styles.details
        )}
      </Box>
      {alert.cta_enabled && (
        <Box sx={styles.cta(appearance)}>{copy.cta_text || 'Go'}</Box>
      )}
    </Box>
  );
}

AlertPreview.propTypes = {
  alert: PropTypes.shape({
    languages: PropTypes.shape({}),
    appearance: PropTypes.string,
    icon: PropTypes.string,
    cta_enabled: PropTypes.bool,
  }).isRequired,
  language: PropTypes.string,
  onChange: PropTypes.func,
};

AlertPreview.defaultProps = {
  language: DEFAULT_LANGUAGE,
  onChange: null,
};
